import {
  flow,
  getParent,
  getRoot,
  type Instance,
  types,
} from 'mobx-state-tree';

import { fetchApplicationEmails } from 'src/graphql/hirer/fetchApplicationEmails';
import type { PageConfig } from 'src/libs/PageConfig';

import { getDashisedBucket } from '../../constants/jobApplicationStatus';
import type { IStore } from '../Store';
import {
  ACTION_DONE,
  ACTION_ERROR,
  STATE_ERROR,
  STATE_LOADED,
  stateType,
} from '../state';

import Email from './Email';
import type { IJobApplication } from './JobApplication';
import { sendEmailForSingleApplication } from './utils/emailAction';

const ProspectEmailStore = types
  .model('ProspectEmailStore', {
    emailCount: types.optional(types.number, 0),
    emailList: types.array(Email),
    state: stateType,
  })
  .views((self) => ({
    get rootStore() {
      return getRoot(self) as IStore;
    },

    get jobApplication(): IJobApplication {
      return getParent(self) as IJobApplication;
    },

    get trackingAttributes(): {
      candidateId: string;
      bucket: string;
      prospectId: string;
    } {
      const bucket = getDashisedBucket(
        this.rootStore.jobApplicationStore.bucket,
      );
      return {
        ...(this.jobApplication as IJobApplication).baseTrackingAttributes,
        bucket,
        prospectId: this.jobApplication.id,
      };
    },
  }))
  .actions((self) => {
    const loadEmails: (
      addCustomError: PageConfig['dataDogRum']['addCustomError'],
    ) => Promise<unknown[]> = flow(function* (
      addCustomError: PageConfig['dataDogRum']['addCustomError'],
    ) {
      try {
        const emails = yield fetchApplicationEmails({
          adcentreCandidateId: (self.jobApplication as IJobApplication)
            .candidateId,
          prospectId: (self.jobApplication as IJobApplication).id,
        });
        if (emails && emails.length) {
          self.emailList = emails;
          self.emailCount = emails.length;
        }
        self.state = STATE_LOADED;
        return emails;
      } catch (error: any) {
        self.state = STATE_ERROR;
        addCustomError({
          error,
          context: {
            activity: 'LOAD_EMAILS',
            description: 'Error attempting: fetchApplicationEmails',
          },
        });
      }
    });

    const increaseEmailCount = () => {
      self.emailCount = self.emailCount + 1;
    };

    const sendEmail: (params: {
      subject: string;
      message: string;
      enableReplies: boolean;
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) => Promise<'done' | 'error'> = flow(function* ({
      subject,
      message,
      enableReplies,
      addCustomError,
    }: {
      subject: string;
      message: string;
      enableReplies: boolean;
      addCustomError: PageConfig['dataDogRum']['addCustomError'];
    }) {
      try {
        const sentEmail = yield sendEmailForSingleApplication(
          (self.jobApplication as IJobApplication).id,
          subject,
          message,
          enableReplies,
          self.trackingAttributes,
        );
        self.emailList.unshift(sentEmail);
        self.emailCount = self.emailCount + 1;
        return ACTION_DONE;
      } catch (error: any) {
        addCustomError({
          error,
          context: {
            activity: 'SEND_EMAIL',
            description: 'Error attempting: sendEmailForSingleApplication',
          },
        });
        return ACTION_ERROR;
      }
    });

    return { loadEmails, sendEmail, increaseEmailCount };
  });

export type IProspectEmailStore = Instance<typeof ProspectEmailStore>;

export default ProspectEmailStore;
